import { FC } from 'react';

import { useTrackChanges } from 'modules/segment/hooks';
import {
  useSearchParamState,
  serializationOptionsForStringValue,
} from 'utils/hooks/useSearchParamState';

import { QUERY_PARAMS } from '../constants';
import { SnippetFiltersContext, SnippetFiltersState } from './SnippetFiltersContext';

export const SnippetFiltersContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  /**
   * Search input state
   *
   * To avoid sending a GraphQL query on each keystroke from the search text input,
   * we need to debounce mutations of the `savedSearch` state. However, we also
   * need the text input to re-render each time the user triggers a keystroke.
   * Therefore, for the search input, we maintain two states: one that is debounced
   * (used for triggering new GraphQL queries + is stored in URL search params) and one
   * that is set immediumtely on keystroke (used for rendering the text input).
   */
  const [searchQuery, setSearchQuery] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.SEARCH,
    stateValueWhenSearchParamIsUnset: '',
    pushChangesToBrowserHistory: true,
    ...serializationOptionsForStringValue,
  });

  // Track search changes
  useTrackChanges({
    eventName: 'Changed Search Query',
    getEventProperties: ([prevSavedSearch]) => ({
      previousSearchQuery: prevSavedSearch,
      currentSearchQuery: searchQuery,
    }),
    trackedValues: [searchQuery],
  });

  const context: SnippetFiltersState = {
    searchQuery,
    setSearchQuery,
  };

  return (
    <SnippetFiltersContext.Provider value={context}>{children}</SnippetFiltersContext.Provider>
  );
};
